import { hgApi } from "@/api/api";
import { parseError, setUnauthorizedHandler, throwIfNotOk } from "@/api/errors";
import { notifyErrorWith, notifySignedOut } from "@/common/notify";
import { forgetAllTokens } from "@/providers/external/tokens";
import { forgetExternalBodies } from "@/providers/external/useExternalRecordBody";
import { HGMe } from "@/common/types";
import { setMe, setUserInfo } from "@/state/redux/slices/ui";
import { store } from "@/state/redux/store";
import { jwtDecode } from "jwt-decode";
import type { CredentialResponse } from "@react-oauth/google";

export class LoginError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export const authorize = (credentialResponse: CredentialResponse) => {
  const newIdToken = credentialResponse.credential;
  if (!newIdToken) return;
  const userInfo = jwtDecode(newIdToken);
  localStorage.setItem("userInfo", JSON.stringify(userInfo));
  store.dispatch(setUserInfo(userInfo as Record<string, string>));
  login(newIdToken).catch(notifyErrorWith("Google sign-in failed"));
};

export const login = (idToken: string) => {
  return fetch("/api/login-google", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })
    .then(throwIfNotOk)
    .then(checkAuthorization);
};

export const loginWithPassword = async (
  username: string,
  password: string,
): Promise<HGMe> => {
  const response = await fetch("/api/login-password", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body -- the status alone has to do
    }
    // Not throwIfNotOk: a 401 here is a wrong password, not an expired
    // session, and must not be reported as one. (403 is what an older server
    // sends for the same thing.)
    const fallback =
      response.status === 401 || response.status === 403
        ? "Invalid username or password."
        : undefined;
    const { message } = parseError(
      { status: response.status, data: body },
      fallback,
    );
    throw new LoginError(response.status, message);
  }

  const me = await checkAuthorization();
  if (!me) {
    throw new LoginError(
      response.status,
      "Signed in, but the session could not be read.",
    );
  }
  return me;
};

export const logout = () => {
  store.dispatch(hgApi.util.resetApiState());
  // Otherwise a Google user's name/avatar linger in the sidebar after a
  // different (e.g. password) user signs in next.
  localStorage.removeItem("userInfo");
  store.dispatch(setUserInfo({}));
  // Provider access tokens and fetched message bodies live outside Redux, so
  // resetApiState does not touch them. Both are keyed to the account that is
  // signing out and neither must survive into the next one's session.
  forgetAllTokens();
  forgetExternalBodies();
  return fetch("/api/logout", { credentials: "include" }).finally(() => {
    return checkAuthorization();
  });
};

declare global {
  interface Window {
    /**
     * The session lookup index.html starts before the bundle is even parsed.
     * Consumed once, by the boot-time call below; every later call (login,
     * logout) has to go to the network for itself.
     */
    __hgSession?: Promise<Response | null>;
  }
}

// Never throws: main.tsx awaits this before the first render, and a network
// blip at boot must not stop the app from mounting signed-out.
export const checkAuthorization = async (): Promise<HGMe | null> => {
  let me: HGMe | null = null;
  const prefetched = window.__hgSession;
  window.__hgSession = undefined;
  try {
    const response =
      (await prefetched) ??
      (await fetch("/api/user/", { credentials: "include" }));
    if (response.ok) {
      me = (await response.json()) as HGMe;
    }
  } catch {
    me = null;
  }
  store.dispatch(hgApi.util.resetApiState());
  store.dispatch(setMe(me));
  return me;
};

// The server has stopped recognising the session -- it expired, or was revoked
// from another tab. Drop to signed-out the way logout does, minus the request
// to /logout, and say so once. Anonymous visitors get 401s from endpoints
// that need a session as a matter of course, so only a session we believed
// in is worth a message.
setUnauthorizedHandler(() => {
  if (!store.getState().ui.me) return;
  forgetAllTokens();
  forgetExternalBodies();
  store.dispatch(hgApi.util.resetApiState());
  store.dispatch(setMe(null));
  notifySignedOut();
});

export const isTokenExpired = (idToken: string) => {
  try {
    const decoded = jwtDecode(idToken);
    if (!decoded.exp) return true;
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    return decoded.exp < currentTime;
  } catch (error) {
    console.error("Token decode error:", error);
    return true; // If decoding failed, consider the token expired
  }
};
